import { useState } from 'react';
import { Alert } from 'antd';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@shared/ui';
import {
  getKycSubmitGate,
  isKycActionable,
  useSubmitKycMutation,
  type ManufacturerProfile,
} from '@entities/manufacturer';
import { ApiError } from '@shared/api';
import { KycBadge } from '../../lib/kyc-badge';

export function KycTab({ profile, isFormDirty }: { profile: ManufacturerProfile; isFormDirty: boolean }) {
  const { t } = useTranslation();
  const submitMutation = useSubmitKycMutation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const gate = getKycSubmitGate(profile);
  const actionable = isKycActionable(profile.kycStatus);

  const description =
    profile.kycStatus === 'PENDING'
      ? t('kyc.pendingHint')
      : profile.kycStatus === 'VERIFIED'
        ? t('kyc.verifiedHint')
        : profile.kycStatus === 'REJECTED'
          ? t('kyc.rejectedHint')
          : t('kyc.noneHint');

  return (
    <div className="flex flex-col gap-4">
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base">{t('kyc.statusTitle')}</CardTitle>
            <CardDescription>{description}</CardDescription>
          </div>
          <KycBadge status={profile.kycStatus} />
        </CardHeader>
        {profile.kycStatus === 'REJECTED' && profile.kycReason ? (
          <CardContent>
            <Alert type="error" showIcon message={t('kyc.statusTitle')} description={profile.kycReason} />
          </CardContent>
        ) : null}
      </Card>

      {actionable ? (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t('kyc.submitTitle')}</CardTitle>
            <CardDescription>{t('kyc.submitHint')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            {gate.missing.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message={
                  <>
                    <p className="mb-2 font-medium">{t('kyc.missingTitle')}</p>
                    <ul className="list-disc pl-5">
                      {gate.missing.map((key) => (
                        <li key={key}>{t(`kyc.missing.${key}`)}</li>
                      ))}
                    </ul>
                  </>
                }
              />
            ) : null}

            {isFormDirty ? (
              <Alert type="warning" showIcon message={t('kyc.unsavedWarning')} />
            ) : null}

            {submitError ? <Alert type="error" showIcon message={submitError} /> : null}

            <Button
              type="button"
              disabled={!gate.canSubmit || isFormDirty}
              loading={submitMutation.isPending}
              onClick={() => {
                setSubmitError(null);
                submitMutation.mutate(undefined, {
                  onError: (err) =>
                    setSubmitError(err instanceof ApiError ? err.message : 'Submission failed'),
                });
              }}
            >
              {t('kyc.submit')}
            </Button>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
